import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {deselectBarracksForRecruitment, deselectUnit, setSelectedToken, showToast} from '../../../redux/actions';
import {
  selectCurrentPlayer,
  selectPendingRecruitmentActions,
  selectSelectedUnit
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {getUnitSizeWithPendingRecruitment} from "../../../utils/gameState/recruitment/getUnitSizeWithPendingRecruitment";
import './WarRecruitmentStageToolbar.css'
import {checkIfNewUnitSelected} from "../../../utils/gameState/checkIfNewUnitSelected";
import {getUnitIconClassNameForButton} from "../../../utils/unit/getUnitIconClassNameForButton";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {selectSelectedBarracksHexKey, selectSelectedToken} from "../../../redux/reducers/privateStateReducer/selectors";
import {recruitNewUnitOnBarracks} from "../../../utils/gameState/recruitment/recruitNewUnitOnBarracks";
import store from "../../../redux/store";
import {getUnitTypesForRecruitment} from "../../../utils/unit/getUnitTypesForRecruitment";
import UNIT_TYPES, {checkIfInfantryUnitType, getUnitPrice, UNIT_TYPES_ROW_ORDER} from "../../../configs/units";
import styles from "../Toolbar.styles";
import UnitIcon from "./UnitIcon/UnitIcon";
import {getUnitMaxSizeForPlayer} from "../../../utils/unit/getUnitMaxSizeForPlayer";
import {calculateAvailableRecruitmentCells} from "../../../utils/gameState/calculateAvailableRecruitmentCells";
import {HexButtonFrame} from '../../gameUi/HexButton';
import {joinClassNames} from '../../gameUi/joinClassNames';
import {getPlayerColorStyle} from '../../../utils/gameUi/getPlayerColorStyle';
import {checkIfToastHintHidden, TOAST_HINTS} from '../../../utils/toast/toastHintsStorage';
import {checkIfColorBlendsWithStone} from '../../../utils/gameUi/checkIfColorBlendsWithStone';
import {useDesktopLayout} from '../../../hooks/visuals/useWideScreenLayout';
import RecruitUnitCard from './RecruitUnitCard/RecruitUnitCard';
import {
  countTokenGroupGaps,
  getTokenRowSlots,
  TokenGlint,
  TokenPrice,
  TokenSpotlight
} from '../TokenMuster/TokenMuster';

// Группа типа в ряду: между группами зазор шире
const getUnitTypeGroup = (unitTypeId) => {
  if (checkIfInfantryUnitType(unitTypeId)) {
    return 'infantry';
  }
  return unitTypeId === UNIT_TYPES.D4.id ? 'catapult' : 'cavalry';
};

// Типы по порядку ряда с отсутствующими (гномы без кавалерии, великаны с одним типом):
// первый в группе отмечен, перед ним зазор шире
const getRowTokens = (unitTypes) => {
  const orderedIds = unitTypes
    .map(({id}) => id)
    .sort((a, b) => UNIT_TYPES_ROW_ORDER.indexOf(a) - UNIT_TYPES_ROW_ORDER.indexOf(b));
  return orderedIds.map((id, index) => ({
    id,
    isGroupStart: index > 0 && getUnitTypeGroup(id) !== getUnitTypeGroup(orderedIds[index - 1]),
  }));
};

// Наведение мышью на жетон показывает его тип в карточке. Пока карточка
// закрыта, ждем немного: мышь, проходящая через ряд к другой кнопке, не должна
// дергать плиту. Уход с ряда — тоже с задержкой: зазоры между жетонами не должны
// гасить карточку
const HOVER_OPEN_DELAY_MS = 110;
const HOVER_CLOSE_DELAY_MS = 170;

const discountIcon = (
  <img
    className={'toolbar_discount'}
    src={'/img/icons/percent white.png'}
    alt={'Скидка'}
    title={'Скидка'}
  />
);

const WarRecruitmentStageToolbar = () => {
  const dispatch = useDispatch();
  const isDesktopLayout = useDesktopLayout();
  const [hoveredTypeId, setHoveredTypeId] = useState(null);
  const hoverTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(hoverTimerRef.current), []);
  const selectedToken = useSelector(selectSelectedToken);
  const selectedBarracksHexKey = useSelector(selectSelectedBarracksHexKey);
  const selectedUnit = useSelector(selectSelectedUnit);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const pendingRecruitmentActions = useSelector(selectPendingRecruitmentActions);
  // Казармы, которые подсветятся на поле: свои, свободные и не выключенные
  // противником или повреждением (тот же расчет, что и у подсветки в
  // GameScene). Селектор отдает булево, а не список: новый массив на каждом
  // вызове перерисовывал бы тулбар на любое изменение стора
  const hasAvailableBarracks = useSelector(
    (state) => calculateAvailableRecruitmentCells(state).length > 0
  );

  const unitTypes = getUnitTypesForRecruitment(currentPlayer);
  // Белая обводка силуэтов на жетонах типов — у темных цветов игрока и голубого:
  // без нее они сливаются с каменным гексом, а светлым она не нужна
  const isUnitIconOutlined = checkIfColorBlendsWithStone(currentPlayer.color);

  const getNewUnitForStatsDisplay = (unitTypeId) => {
    const unitType = unitTypes.find(item => item.id === unitTypeId);
    const {price, isDiscount} = getUnitPrice(unitTypeId, currentPlayer);
    return {
      ...unitType,
      price,
      isDiscount,
      id: undefined,
      type: unitType.id,
      size: 1,
      maxSize: getUnitMaxSizeForPlayer(unitType.id, currentPlayer),
    };
  };

  const isNewUnitSelected = checkIfNewUnitSelected(selectedToken);
  let unitForStatsDisplay;
  if (isNewUnitSelected) {
    unitForStatsDisplay = getNewUnitForStatsDisplay(selectedToken);
  } else if (selectedUnit) {
    const unitType = unitTypes.find(item => item.id === selectedUnit.type)
    unitForStatsDisplay = {
      ...unitType,
      id: selectedUnit.id,
      ownerId: selectedUnit.ownerId,
      // factionId/capturedFromOwnerId - для отображения иконки соблазнённого
      // Суккубом отряда так же, как на игровом поле (см. UnitIcon,
      // getUnitIconDisplayProps)
      factionId: selectedUnit.factionId,
      capturedFromOwnerId: selectedUnit.capturedFromOwnerId,
      hexKey: selectedUnit.hexKey,
      type: unitType.id,
      size: getUnitSizeWithPendingRecruitment(selectedUnit, pendingRecruitmentActions),
      maxSize: selectedUnit.maxSize,
      isCurrentPlayerOwner: selectedUnit.ownerId === currentPlayer.id,
    }
  }

  // Обработчик выбора типа отряда для найма
  const handleRecruitmentSelect = (unitTypeId) => {
    // Сбрасываем выбор отряда на поле
    dispatch(deselectUnit());

    // Обратный порядок действий: казарма уже выбрана кликом по полю (см.
    // GameScene) — выбор типа сразу нанимает на нее отряд. Казарму снимаем в
    // любом случае: и после успешного найма, и после отказа (не хватило монет,
    // уперлись в лимит типа) — иначе следующий клик по типу молча повторял бы
    // ту же ошибку
    if (selectedBarracksHexKey) {
      dispatch(deselectBarracksForRecruitment());
      recruitNewUnitOnBarracks(
        store,
        REGISTRY_SINGLETON.getSoundManager(),
        selectedBarracksHexKey,
        unitTypeId
      );
      return;
    }

    REGISTRY_SINGLETON.getSoundManager().playUnitRecruitmentTypeSelect();
    // Сохраняем выбранный тип отряда
    dispatch(setSelectedToken(unitTypeId));

    // Оба тоста ниже — подсказки для новичка: каждую игрок может отключить
    // ссылкой в самом тосте, флаги у них раздельные

    // Подсвечивать нечего — объясняем, чего не хватает, вместо предложения
    // выбрать казарму: своих свободных и работающих у игрока сейчас нет
    if (!hasAvailableBarracks) {
      if (!checkIfToastHintHidden(TOAST_HINTS.RECRUITMENT_NO_FREE_BARRACKS)) {
        dispatch(showToast(
          'Чтобы нанять новый отряд, нужно иметь свободную казарму - у тебя такой нет.',
          'info',
          3000,
          true,
          false,
          TOAST_HINTS.RECRUITMENT_NO_FREE_BARRACKS
        ));
      }
      return;
    }

    if (checkIfToastHintHidden(TOAST_HINTS.RECRUITMENT_SELECT_BARRACKS)) {
      return;
    }

    dispatch(showToast(
      'Выберите одну из выделенных казарм для размещения выбранного отряда.',
      'info',
      5000,
      true,
      false,
      TOAST_HINTS.RECRUITMENT_SELECT_BARRACKS
    ));
  };

  // Наведение — только мышью: на сенсорном экране шире 1024 px касание сразу
  // выбирает тип, а «ухода» с жетона после него не бывает
  const handleTokenPointerEnter = (event, unitTypeId) => {
    if (event.pointerType !== 'mouse') {
      return;
    }
    clearTimeout(hoverTimerRef.current);
    if (hoveredTypeId || unitForStatsDisplay) {
      setHoveredTypeId(unitTypeId);
      return;
    }
    hoverTimerRef.current = setTimeout(() => setHoveredTypeId(unitTypeId), HOVER_OPEN_DELAY_MS);
  };

  const handleRowPointerLeave = () => {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHoveredTypeId(null), HOVER_CLOSE_DELAY_MS);
  };

  // Что показывает карточка: тип под мышью важнее выбранного
  const hoveredUnit = isDesktopLayout && hoveredTypeId && unitTypes.some(({id}) => id === hoveredTypeId)
    ? getNewUnitForStatsDisplay(hoveredTypeId)
    : null;
  const cardUnit = hoveredUnit || unitForStatsDisplay;

  const rowTokens = getRowTokens(unitTypes);
  const rowSlots = getTokenRowSlots(rowTokens);
  // Вне десктопа футер во всю ширину: карточка над рядом
  const isStackedCard = !isDesktopLayout;
  const spotlightSlot = cardUnit && rowSlots[cardUnit.type];
  const isTypeUnaffordable = (unitTypeId) => getUnitPrice(unitTypeId, currentPlayer).price > currentPlayer.coins;

  return (
    <div
      className={joinClassNames(
        'new-units-toolbar',
        'new-units-toolbar_muster',
        isStackedCard && 'new-units-toolbar_muster_stacked',
      )}
    >
      <RecruitUnitCard
        unit={cardUnit}
        isStacked={isStackedCard}
        // У нанятого отряда цена — докупка размера, ее проверяет своя логика
        isUnaffordable={Boolean(cardUnit && !cardUnit.id && isTypeUnaffordable(cardUnit.type))}
      />
      <div
        className={'new-units-toolbar_units token-muster'}
        style={{
          ...getPlayerColorStyle(currentPlayer.color),
          '--token-count': unitTypes.length,
          '--token-groups': countTokenGroupGaps(rowTokens),
        }}
        onPointerLeave={isDesktopLayout ? handleRowPointerLeave : undefined}
      >
        <TokenSpotlight spot={spotlightSlot}/>
        {unitTypes.map(token => (
          <div
            key={token.id}
            // Не по карману — приглушен силуэт отряда, как цена строения в
            // строительстве и политики на рынке: тип выбирается, а нанять не
            // даст проверка (монеты за нанятое в этом ходу уже списаны)
            className={joinClassNames(
              'new-units-toolbar_unit-container',
              'token-muster_item',
              isUnitIconOutlined && 'new-units-toolbar_unit-container_outlined',
              isTypeUnaffordable(token.id) && 'new-units-toolbar_unit-container_unaffordable',
              isTypeUnaffordable(token.id) && 'token-muster_item_unaffordable',
              rowSlots[token.id].isGroupStart && 'token-muster_item_group-start',
            )}
            style={{
              '--unit-row-order': UNIT_TYPES_ROW_ORDER.indexOf(token.id),
              '--slot-index': rowSlots[token.id].slot,
            }}
            data-tutorial-select={token.id}
          >
            <button
              className={joinClassNames('gui-hex-button', 'gui-hex-button_stone', 'toolbar-button', selectedToken === token.id && 'gui-hex-button_selected')}
              style={getPlayerColorStyle(currentPlayer.color)}
              aria-pressed={selectedToken === token.id}
              onClick={() => handleRecruitmentSelect(token.id)}
              onPointerEnter={isDesktopLayout ? (event) => handleTokenPointerEnter(event, token.id) : undefined}
              title={token.description}
            >
              <div style={styles.tokenContent}>
                <UnitIcon
                  unitType={token.id}
                  playerColor={currentPlayer.color}
                  factionId={currentPlayer.factionId}
                  hasUnitSkin={currentPlayer.hasUnitSkin}
                  className={getUnitIconClassNameForButton(token.id)}
                />
              </div>
              {/* Рамка — сосед обертки иконки: обводка и приглушение силуэта
                  (filter на обертке, WarRecruitmentStageToolbar.css) не должны
                  ее захватывать */}
              {selectedToken === token.id ? <HexButtonFrame className={'cell-icon'}/> : null}
              <TokenGlint/>
              <TokenPrice price={token.price}>
                {token.isDiscount && discountIcon}
              </TokenPrice>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WarRecruitmentStageToolbar;